import { LINK_KEY, SELECTION_KEY } from '@techchange/pm-specs';

import { linkSelectionPluginKey } from './plugins/linkSelection';

/**
 * Toggles the link input tooltip unless the selection is empty.
 *
 * @param {Object} state     The current editor state.
 * @param {Object} dispatch  The editor dispatch.
 *
 * @returns {boolean} Whether or not the command was successful.
 *
 */
export const toggleTooltip = (state, dispatch) => {

	const { tr, selection, schema } = state;

	const { from, to, node } = selection;

	const pluginState = linkSelectionPluginKey.getState(state);

	const { showTooltip } = pluginState;

	// Only allow linking if this is a TextSelection that spans at least one text node
	if (from !== to && !node) {

		tr.setMeta('toggleLinkTooltip', true);

		if (showTooltip) {

			tr.removeMark(from, to, schema.mark(SELECTION_KEY, { visible: true }));

		} else {

			tr.addMark(from, to, schema.mark(SELECTION_KEY, { visible: true }));

		}

	}

	if (dispatch) {

		dispatch(tr);

	}

	return true;

};

/**
 * Adds link mark to the specified selection range. If no selection
 * range is provided, adds link mark to the current selection.
 *
 * @param {Object} attrs    The link attributes.
 * @param {number} [start]  The start position of the node to mark.
 * @param {number} [end]		The end position of the node to mark.
 *
 * @returns {Object} Parametrized command to embed the link.
 */
export const embedLink = (attrs, start, end) => {

	return (state, dispatch) => {

		const { schema, selection, tr } = state;

		const linkType = schema.marks[LINK_KEY];
		const selectionType = schema.mark(SELECTION_KEY, { visible: true });

		const { from, to, node } = selection;

		// Remove any exisiting link marks, add the new link mark
		tr.removeMark(start || from, end || to, linkType);
		tr.addMark(start || from, end || to, linkType.create(attrs));

		// Only toggle tooltip for text selections
		if (!node) {

			tr.setMeta('toggleLinkTooltip', true);
			tr.removeMark(start || from, end || to, selectionType);

		}

		if (dispatch) {

			dispatch(tr);

		}

		return true;

	};

};

/**
 * Removes link mark from the specified selection range and hides
 * the link input tooltip.
 *
 * @param {number} start  The start position of the node to unmark,
 * @param {number} end    The end position of the node to unmark.
 *
 * @returns {Object} Parametrized command to remove the link.
 */
export const removeLink = (start, end) => {

	return (state, dispatch) => {

		const { schema, tr, selection } = state;
		const { from, to, node } = selection;

		const linkType = schema.marks[LINK_KEY];

		tr.removeMark(start || from, end || to, linkType);

		// Hide tooltip and deselect link for text selections
		if (!node) {

			tr.setMeta('toggleLinkTooltip', true);
			tr.setMeta('selectedLink', null);
			tr.setMeta('linkStart', null);
			tr.setMeta('linkEnd', null);

		}

		if (dispatch) {

			dispatch(tr);

		}

		return true;

	};

};
